import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, MenuItem, Paper, Stack, TextField, Tooltip, IconButton, Typography,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { request, useOperations } from './OperationsProvider';
import RouteDetail from './RouteDetail';
import useOperationalPermissions from '../common/util/operationalPermissions';
import { deviceAvailable, latestAssignment } from './devices';
import { DocumentWarning, documentsInState } from './documents';
import AddVehicleDialog, { blankVehicle } from './AddVehicleDialog';
import ScanChallan from './ScanChallan';
import { warmScanner } from './scan/ocr';
import useDraft from '../common/util/useDraft';
import FormRow from '../common/components/FormRow';
import CatalogField from './CatalogField';

// Admitting a truck is one operation, used at two desks. The parking counter admits a truck that
// will wait in the yard; the gate admits one that arrived straight there and is entering now, so the
// entry stage is recorded with it. Everything else — challan, order matching, driver, route and
// tracking — is identical, and lives here once rather than twice.

// crypto.randomUUID exists only on HTTPS or localhost; getRandomValues works on any origin,
// so a plain-HTTP deployment still gets a valid version 4 request ID.
const newRequestId = () => {
  if (window.crypto.randomUUID) return window.crypto.randomUUID();
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] % 16) + 64;
  bytes[8] = (bytes[8] % 64) + 128;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

// The server compares challans and vehicle numbers the same way: case, spacing and punctuation
// in a vehicle number do not make a different vehicle.
const challanKey = (value) => String(value || '').trim().toUpperCase().replace(/\s+/g, ' ');
const vehicleKey = (value) => String(value || '').toUpperCase().replace(/[^\p{L}\p{N}]/gu, '');
// What a manual entry cannot do without. Everything else on the paperwork - the consignment number,
// the material, the tonnage - is recorded where the challan carries it and left alone where it does
// not, because a counter that cannot admit a truck without inventing a figure will invent one.
// What the server will refuse the trip without, so the checklist says "Needed" for exactly the
// fields that would otherwise come back as a 400 after the desk pressed Admit. A supplier is asked
// of an arriving load and a transporter of a departing one, which is why the set depends on the
// movement rather than being a fixed list.
export const requiredFor = (movement) => ['consignor', 'consignee', ...(movement === 'UNLOAD' ? ['supplier'] : [])];

// A truck arriving at the outside parking is written down on its number plate; the challan can
// follow. A truck going into the works cannot - past that barrier the load is weighed and tipped
// against that paper, and a trip that got in without it cannot be reconciled afterwards.
//
// So the parking desk asks for these and does not insist, and the gate desk insists. The server
// draws the same line in the same place: it waives them for a parking intake and collects them when
// the first stage of the route is recorded.
export const challanDueNow = (desk) => desk === 'GATE';

// Whether a portable device can be issued, and what the counter sees beside it. With a tracking
// provider connected the server refuses a device without a recent fix; without one it cannot tell.
export const reporting = (source, data) => {
  if (!data.trackingConnected) return { ok: true, text: 'reporting not checked' };
  if (!source.providerId) return { ok: false, text: 'not linked to VTS' };
  if (!source.lastFixAt) return { ok: false, text: 'never reported' };
  const minutes = Math.round((Date.now() - Date.parse(source.lastFixAt)) / 60000);
  if (minutes * 60000 > (data.reportingWindowMs || 900000)) return { ok: false, text: `last fix ${minutes} min ago` };
  return { ok: true, text: minutes < 1 ? 'reporting now' : `reporting · ${minutes} min ago` };
};

// Plates are written with spaces, dashes and both cases, and read back the same way only by luck.
const plainly = (value) => String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

// The moment the counter is working at, to the minute, in the browser's own clock - which at a
// plant is the plant's clock. Written the way an input[type=datetime-local] wants it.
const now = () => {
  const at = new Date(Date.now() - new Date().getTimezoneOffset() * 60000);
  return at.toISOString().slice(0, 16);
};
// What a queue of trucks has in common.
//
// Twelve lorries waiting at a gate are usually the same lane: the same supplier sending the same
// material to the same consignee at the same destination. The form reset to blank after every
// admission, so a desk under pressure retyped six catalogue pickers per truck, and the slowest part
// of admitting a lorry was re-entering what had not changed since the one before it.
//
// So the lane is carried from the last truck admitted at this desk, prefilled and obvious, with one
// click to clear it. Never the shipment, the challan, the vehicle or the driver - those identify
// this truck, and a desk that tabbed past a carried-over vehicle number would admit the wrong one.
const LANE = ['supplier', 'transporter', 'consignor', 'consignee', 'destination', 'material', 'quantityUnit', 'movementType', 'direction'];
// Keyed by workspace and account as well as desk. A shared terminal at a counter is signed into by
// whoever is on shift, and sometimes by a different customer's staff entirely; a key naming only the
// desk offered one workspace's consignor, consignee and material to the next person who sat down.
const laneKey = (desk, user) => `intake.lane.${user?.tenantId ?? 'none'}.${user?.id ?? 'none'}.${desk}`;
const rememberLane = (desk, form, user) => {
  try {
    window.localStorage.setItem(laneKey(desk, user), JSON.stringify(Object.fromEntries(
      LANE.filter((k) => String(form[k] ?? '').trim() !== '').map((k) => [k, form[k]]),
    )));
  } catch { /* a desk that cannot store it simply types it again */ }
};
const lastLane = (desk, user) => {
  try { return JSON.parse(window.localStorage.getItem(laneKey(desk, user)) || '{}') || {}; } catch { return {}; }
};
const blankDraft = (desk) => ({ desk, requestId: newRequestId(), recordSource: 'MANUAL', movementType: 'UNLOAD', direction: 'INBOUND', quantityUnit: 't', challanAt: now() });
const draftWithLane = (desk, user) => ({ ...blankDraft(desk), ...lastLane(desk, user) });

// `scan` false where the surrounding page already offers one — the gate desk scans a challan to
// find the trip it belongs to, and hands the same read here when no trip answers to it. Two buttons
// that photograph the same piece of paper is a choice the operator should never have to make.
const AdmitTruck = ({
  desk = 'YARD', sidebar = null, onAdmitted, scan = true, scanned = null, compact = false,
}) => {
  const {
    data, trips, refresh, error: loadError,
  } = useOperations();
  const { operate } = useOperationalPermissions();
  const navigate = useNavigate();
  const atGate = desk === 'GATE';
  // Read before the draft is seeded: the carried-over lane belongs to this account at this desk, so
  // the account has to be known to find it.
  const user = useSelector((state) => state.session.user);
  // Drafts are kept per desk, so a half-filled parking form never reappears at the gate.
  const [form, setForm, discardDraft, draftError] = useDraft(`intake.${desk}`, draftWithLane(desk, user));
  // The desk is open long before a truck arrives; the scanner's models are fetched in that time so
  // that pressing Scan is the moment it starts reading, not the moment it starts downloading.
  useEffect(() => {
    if (!scan) return undefined;
    const idle = window.requestIdleCallback || ((run) => window.setTimeout(run, 1200));
    const handle = idle(() => warmScanner());
    return () => (window.cancelIdleCallback || window.clearTimeout)(handle);
  }, [scan]);
  useEffect(() => {
    const warn = (event) => { if (Object.keys(form).length > 6) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', warn); return () => window.removeEventListener('beforeunload', warn);
  }, [form]);
  const [pending, setPending] = useState(false); const [error, setError] = useState('');
  const fleetWide = useSelector((state) => (state.session.user?.vehicleIds || []).some((id) => String(id) === '*'));
  const [newVehicle, setNewVehicle] = useState(null);
  const [scanning, setScanning] = useState(false);
  // The fields a counter does not fill in: the route, which the destination decides, and the
  // direction, which the movement decides. They are on the form, behind one line, for the loads
  // that run differently - rather than in front of every truck that does not.
  const [advanced, setAdvanced] = useState(false);
  const [fittedNotice, setFittedNotice] = useState('');
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const field = (key, label, options, required = true) => (
    <TextField fullWidth required={required} disabled={pending} label={label} value={form[key] || ''} select={!!options} onChange={(e) => set(key, e.target.value)} inputProps={{ maxLength: 500 }}>
      {options?.map(([id, name, disabled]) => <MenuItem key={id} value={id} disabled={!!disabled}>{name}</MenuItem>)}
    </TextField>
  );
  // An empty dropdown is not an answer. Where nothing can be chosen, the field says so and the
  // message names the reason and the screen that fixes it, instead of leaving the counter guessing.
  const picker = (key, labelText, options, empty) => (
    <Box>
      <TextField fullWidth required select disabled={pending || !options.length} label={labelText} value={options.length ? (form[key] || '') : ''} onChange={(e) => set(key, e.target.value)}>
        {options.map(([id, name, disabled]) => <MenuItem key={id} value={id} disabled={!!disabled}>{name}</MenuItem>)}
      </TextField>
      {!options.length && <Alert severity="warning" sx={{ mt: 1 }} action={empty.action && <Button size="small" sx={{ whiteSpace: 'nowrap' }} onClick={empty.onClick || (() => navigate(empty.path))}>{empty.action}</Button>}>{empty.message}</Alert>}
    </Box>
  );
  // Only an imported order issued for the challan being entered can be used; the server refuses any other.
  const reference = challanKey(form.challanNumber) || challanKey(form.shipmentId);
  const orders = reference
    ? (data.consignments || []).filter((c) => challanKey(c.challanNumber) === reference || challanKey(c.consignmentId) === reference)
    : [];
  const orderIds = JSON.stringify(orders.map((o) => o.consignmentId));
  useEffect(() => {
    if (form.recordSource !== 'ERP') return;
    const ids = JSON.parse(orderIds);
    const chosen = ids.length === 1 ? ids[0] : (ids.includes(form.consignmentId) ? form.consignmentId : '');
    if (chosen !== (form.consignmentId || '')) setForm((current) => ({ ...current, consignmentId: chosen }));
  }, [form.recordSource, form.consignmentId, orderIds]);
  const order = form.recordSource === 'ERP' ? orders.find((o) => o.consignmentId === form.consignmentId) : null;
  const vehicle = data.vehicles.find((v) => v.id === form.vehicleId);
  // Expired paperwork no longer passes on a warning alone. The desk names a reason, the server
  // requires it for exactly these vehicles, and it is kept on the trip.
  const lapsed = documentsInState(vehicle, 'expired');
  const documentsSettled = !lapsed.length || !!String(form.documentWaiverReason || '').trim();
  const vehicleConflict = order?.vehicleNumber && vehicle && vehicleKey(order.vehicleNumber) !== vehicleKey(vehicle.name);
  // A truck already carrying a fitted unit reports position on its own, so nothing is issued to it —
  // it is admitted here like any other, rather than being sent to a different screen.
  const fitted = form.vehicleId ? data.assignments.find((a) => a.vehicleId === form.vehicleId && a.kind === 'GPS' && !a.endedAt) : null;
  const fittedSource = fitted && data.sources.find((s) => s.id === fitted.sourceId);
  const availableVehicles = data.vehicles.filter((v) => !trips.some((t) => t.vehicleId === v.id && !t.closedAt));
  // Most of what arrives is hired for the one load, so the counter types the number painted on the
  // door and nothing else: a registered truck is recognised from the same field, and anything else
  // is registered as a market vehicle when the truck is admitted.
  const plate = plainly(form.vehicleNumber);
  const registered = plate ? data.vehicles.find((v) => plainly(v.name) === plate) : null;
  const busy = registered && trips.find((t) => t.vehicleId === registered.id && !t.closedAt);
  const setPlate = (typed) => setForm((current) => {
    const entered = String(typed || '').toUpperCase();
    const known = data.vehicles.find((v) => plainly(v.name) === plainly(entered));
    return { ...current, vehicleNumber: entered, vehicleId: known ? known.id : undefined };
  });
  // A truck that carries its own unit is tracked whatever the counter does next, and the clerk
  // should know that before reaching for a portable one — so it is said once, in front of them,
  // rather than left to be noticed further down the form.
  useEffect(() => {
    if (fitted && fittedSource && vehicle) setFittedNotice(`${vehicle.name} is fitted with its own GPS device, ${fittedSource.identifier}. It reports position on its own, so no portable unit is issued for this trip.`);
  }, [fitted?.id]);

  // Everything a scan read, into the form: the fields it can type, and the ones it has to choose
  // from what this plant has registered. A value already entered is the clerk's and stands —
  // scanning fills a form in, it does not correct one.
  const applyScan = (current, { challanNumber, imageDataUrl, fields }) => {
    const filled = { ...current };
    if (fields?.driver) filled.scannedDriver = fields.driver;
    const chosen = ['vehicle', 'driver', 'direction'];
    if (!current.quantity && fields?.quantity && fields?.quantityUnit) filled.quantityUnit = fields.quantityUnit;
    Object.entries(fields || {}).forEach(([key, value]) => {
      if (!chosen.includes(key) && !filled[key]) filled[key] = value;
    });
    // The number on the paper is the number the counter would have typed, registered or not.
    const read = plainly(fields?.vehicle);
    const truck = read && data.vehicles.find((v) => plainly(v.name) === read);
    if (read && !filled.vehicleId && !plainly(filled.vehicleNumber)) filled.vehicleNumber = truck ? truck.name : String(fields.vehicle).toUpperCase();
    if (truck && !filled.vehicleId && (!current.vehicleNumber || plainly(current.vehicleNumber) === read)) filled.vehicleId = truck.id;
    const named = plainly(fields?.driver);
    const who = named && data.drivers.find((d) => plainly(d.name) === named);
    if (who && !filled.driverId) { filled.driverId = who.id; filled.driverMobile = who.mobile || who.reference; filled.driverName = who.name; }
    if (!filled.driverName && fields?.driver) filled.driverName = fields.driver;
    if (fields?.direction) {
      filled.direction = fields.direction;
      // A delivery is unloaded here and a despatch is loaded here; both stay changeable.
      filled.movementType = fields.direction === 'OUTBOUND' ? 'LOAD' : 'UNLOAD';
    }
    return {
      ...filled,
      // A code that decodes fills the field; a challan without one leaves whatever was typed alone,
      // so scanning never wipes a number the clerk has already entered.
      challanNumber: current.challanNumber || challanNumber || '',
      challanImageDataUrl: imageDataUrl || current.challanImageDataUrl || '',
    };
  };

  // A challan read elsewhere on the page - the gate desk scans one to look a trip up - lands here
  // whole, so a truck that turns out to have no trip is admitted from the paper already read.
  useEffect(() => {
    if (!scanned) return;
    setForm((current) => applyScan(current, scanned));
  }, [scanned]);
  // A market truck is given whatever the plant has free to put on it - a tracker or a tag. Both are
  // issued the same way, for the one trip, and handed back at the gate.
  const availableSources = data.sources.filter((s) => ['GPS', 'RFID'].includes(s.kind) && deviceAvailable(s, data.assignments));
  // The gate a truck enters by is the first stage of the route it is running, so the clerk chooses
  // the route as usual and the entry gate follows from it.
  const route = data.routes.find((r) => r.id === form.routeId);
  const postedGate = data.checkpoints.find((c) => c.id === user?.postedCheckpointId);
  const routes = postedGate
    ? data.routes.filter((r) => (r.checkpointIds || [])[0] === postedGate.id)
    : data.routes;
  // The route a load runs follows from where it is going, so the counter does not choose it twice:
  // a destination the plant has a route to is matched to that route, and one it has hauled to before
  // is matched to the route those loads took. It is a suggestion in the ordinary sense — it fills the
  // field in, and the clerk can pick another.
  const destinationKey = plainly(form.destination);
  const suggestedRoute = useMemo(() => {
    if (!destinationKey) return null;
    const ends = (r) => (r.checkpointIds || []).map((id) => data.checkpoints.find((c) => c.id === id)).filter(Boolean).filter((c) => c.type === 'CLIENT').slice(-1)[0];
    const named = routes.filter((r) => { const client = ends(r); return client && plainly(client.name) === destinationKey; });
    if (named.length === 1) return { route: named[0], why: `${named[0].name} ends at ${form.destination}` };
    const counts = new Map();
    trips.filter((t) => plainly(t.destination) === destinationKey).forEach((t) => counts.set(t.routeId, (counts.get(t.routeId) || 0) + 1));
    const ranked = [...counts.entries()].sort((a, b) => b[1] - a[1]).find(([id]) => routes.some((r) => r.id === id));
    if (!ranked) return null;
    const chosen = routes.find((r) => r.id === ranked[0]);
    return { route: chosen, why: `${ranked[1]} earlier ${ranked[1] === 1 ? 'load' : 'loads'} to ${form.destination} ran ${chosen.name}` };
  }, [destinationKey, routes.length, trips.length, data.checkpoints.length]);
  // Where the desk is standing already answers most of it.
  //
  // Posted at a gate, the only routes a truck can be admitted on are the ones that start there - and
  // of those, a gate almost always runs mostly one. Until a destination is typed there was nothing
  // chosen at all, so the checklist asked for a route while the field was not even on screen in the
  // simplified form. The gate's usual route is offered instead, and the destination overrides it the
  // moment one is given, because where a load is going is the better answer of the two.
  const gateRoute = useMemo(() => {
    if (routes.length < 2) return null;
    // Counted among loads of the same kind: a delivery coming in and a despatch going out do not
    // run the same way, and the commonest route overall is whichever the plant does more of.
    const counts = new Map();
    trips.filter((t) => !form.movementType || t.movementType === form.movementType).forEach((t) => {
      if (routes.some((r) => r.id === t.routeId)) counts.set(t.routeId, (counts.get(t.routeId) || 0) + 1);
    });
    const ranked = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
    const chosen = ranked && routes.find((r) => r.id === ranked[0]);
    if (!chosen) return null;
    const loads = `${ranked[1]} earlier ${ranked[1] === 1 ? 'load' : 'loads'}`;
    // Said differently depending on what narrowed it, because "where you are standing" and "what
    // this plant mostly does" are different claims and a desk should be able to tell them apart.
    return {
      route: chosen,
      why: postedGate
        ? `${loads} admitted at ${postedGate.name} ran ${chosen.name}`
        : `${loads} of this kind ran ${chosen.name}`,
    };
  }, [postedGate?.id, routes.length, trips.length, form.movementType]);
  useEffect(() => {
    if (!gateRoute || form.routeId) return;
    set('routeId', gateRoute.route.id);
  }, [gateRoute?.route?.id]);
  useEffect(() => {
    if (!suggestedRoute) return;
    // A guess made from the gate yields to one made from the destination; a route the desk picked
    // itself is left alone.
    const guessed = !form.routeId || (gateRoute && form.routeId === gateRoute.route.id);
    if (guessed) set('routeId', suggestedRoute.route.id);
  }, [suggestedRoute?.route?.id]);
  // A plant with one route that a truck can be admitted on has already answered the question. Asking
  // it anyway is how a desk ends up with a mandatory field that has one option in it.
  useEffect(() => {
    if (!form.routeId && routes.length === 1) set('routeId', routes[0].id);
  }, [routes.length]);
  // The driver the number belongs to, where the register already knows him. Matched on digits so a
  // number typed with spaces, dashes or a country code is the same man.
  const digits = (value) => String(value || '').replace(/\D/g, '');
  const knownDriver = form.driverMobile ? data.drivers.find((person) => digits(person.mobile) === digits(form.driverMobile) || digits(person.reference) === digits(form.driverMobile)) : null;
  const setDriverNumber = (typed) => setForm((current) => {
    const match = data.drivers.find((person) => digits(person.mobile) === digits(typed) || digits(person.reference) === digits(typed));
    return {
      ...current,
      driverMobile: typed,
      driverId: match ? match.id : undefined,
      // A registered driver's name is shown rather than asked for again; a name the counter typed
      // for somebody else's number is not carried over to this one.
      driverName: match ? match.name : (current.driverId ? '' : current.driverName),
    };
  });
  const entryGate = route && data.checkpoints.find((c) => c.id === (route.checkpointIds || [])[0]);
  const entryUsable = !atGate || !!entryGate;
  // A gate kept for deliveries does not offer despatch, and a gate that takes both offers both.
  const allDirections = [['INBOUND', 'Inbound'], ['OUTBOUND', 'Outbound']];
  const handles = entryGate?.handles || 'BOTH';
  const directions = handles === 'BOTH' ? allDirections : allDirections.filter(([value]) => value === handles);
  // Direction follows the movement: what is unloaded here came in, what is loaded here goes out. A
  // gate that takes one kind of traffic overrides that, because the server would refuse the other -
  // and that is the one case where the counter needs to see the field at all.
  const implied = form.movementType === 'LOAD' ? 'OUTBOUND' : 'INBOUND';
  const allowed = directions.map(([value]) => value);
  const settled = allowed.includes(implied) ? implied : allowed[0];
  useEffect(() => {
    if (settled && form.direction !== settled) set('direction', settled);
  }, [settled]);
  // Why a list is empty, in the counter's own terms.
  const reasons = (parts) => parts.filter(Boolean).join(', ');
  const gpsUnits = data.sources.filter((s) => s.kind === 'GPS');
  const heldBy = (s, test) => { const latest = latestAssignment(s.id, data.assignments); return latest && test(latest); };
  const issued = gpsUnits.filter((s) => heldBy(s, (a) => !a.endedAt)).length;
  const unreturned = gpsUnits.filter((s) => heldBy(s, (a) => a.endedAt && a.mode === 'TEMPORARY' && !a.returnedAt)).length;
  const unserviceable = gpsUnits.filter((s) => (s.status || 'ACTIVE') !== 'ACTIVE').length;
  const deviceDetail = reasons([issued && `${issued} issued to another truck`, unreturned && `${unreturned} not yet handed back`, unserviceable && `${unserviceable} out of service`]);
  const challanDue = challanDueNow(desk);
  // What the challan says about the load, which the gate demands and the parking takes when it
  // comes. Lifted out of the row below so the row can say both whether it is settled and, at the
  // parking, that it is still to come.
  const orderSettled = form.recordSource === 'ERP'
    ? !!order && !vehicleConflict
    : requiredFor(form.movementType).every((key) => String(form[key] ?? '').trim())
      && (form.movementType !== 'LOAD' || !!String(form.transporter || '').trim())
      && !!String(form.destination || '').trim();
  const checklist = [
    // At the parking these read as "To follow" rather than "Needed": the truck can be admitted
    // without them, and the gate will not let it into the plant until they are entered.
    ['Shipment', !!challanKey(form.shipmentId) || !challanDue, challanDue ? undefined : (challanKey(form.shipmentId) ? undefined : 'To follow')],
    ['Order details', !challanDue || orderSettled, challanDue ? undefined : (orderSettled ? undefined : 'To follow')],
    ['Vehicle', (!!form.vehicleId || plate.length >= 3) && !busy],
    // Three answers, not two. With no truck chosen nothing has been looked at, and saying "Ready"
    // there claimed the paperwork had been checked when there was no paperwork to check. A truck off
    // the street carries no documents on file, which is a fact about the truck rather than a pass.
    ['Documents', documentsSettled && (!!form.vehicleId || plate.length >= 3), (() => {
      if (!form.vehicleId && plate.length < 3) return 'No vehicle yet';
      if (lapsed.length && !String(form.documentWaiverReason || '').trim()) return 'Expired';
      if (lapsed.length) return 'Waived';
      if (vehicle && !(vehicle.documents || []).length) return 'None on file';
      if (!vehicle) return 'Not registered';
      // Nothing to add: the row falls through to the plain green "Ready".
      return undefined;
    })()],
    ['Driver', !!String(form.driverMobile || '').trim()],
    ['Route', !!form.routeId && entryUsable],
    // A tracker has to be reporting before it is worth issuing; a tag reports nothing by design and
    // is ready the moment it is chosen.
    ['Tracking', fitted ? true : (() => {
      const chosen = availableSources.find((s) => s.id === form.sourceId);
      return !!chosen && (chosen.kind !== 'GPS' || reporting(chosen, data).ok);
    })()],
  ];
  const submit = async (event) => {
    event.preventDefault(); setPending(true); setError('');
    const payload = { ...form };
    delete payload.desk;
    if (fitted) delete payload.sourceId;
    if (atGate && entryGate) payload.entryCheckpointId = entryGate.id;
    try {
      const trip = await request('/api/operations/intake', 'POST', payload);
      await refresh();
      // A desk that stays open takes the next truck, so the form is cleared and given a fresh
      // request ID rather than left holding the load that has just been admitted - except for the
      // lane, which the next lorry in the queue almost always shares.
      rememberLane(desk, form, user);
      setForm(draftWithLane(desk, user));
      if (onAdmitted) onAdmitted(trip); else navigate(`/trips/${trip.id}`);
    } catch (e) { setError(e.message); } finally { setPending(false); }
  };
  // Which form the plant has chosen. The simplified desk is the default; a plant that prefers the
  // older sectioned one sets it under Configuration, once, for every desk - two desks filling
  // different forms for the same trip is how one load gets recorded two ways.
  const formId = `admit-${desk}`;
  const simple = (data.config?.tripEntryLayout || 'SIMPLE') !== 'GROUPED';
  // Named so the notice can say what was carried rather than that something was. Movement, direction
  // and unit are defaults rather than a lane, so they are not worth announcing.
  const carriedLane = ['supplier', 'transporter', 'consignor', 'consignee', 'destination', 'material']
    .filter((k) => String(form[k] ?? '').trim() !== '')
    .map((k) => (k === 'consignor' ? 'consignor' : k));
  const [switching, setSwitching] = useState(false);
  const chooseLayout = async (value) => {
    setSwitching(true);
    // Its own endpoint, not the one that writes the whole rules record: that one is an
    // administrator's, and this is a change the desk itself makes.
    try { await request('/api/operations/trip-entry-layout', 'POST', { tripEntryLayout: value }); await refresh(); } catch (e) { setError(e.message); } finally { setSwitching(false); }
  };
  // The blocks the chosen layout moves. Defined once and placed twice, so the
  // simplified desk and the older sectioned one cannot drift apart as the form is changed.
  const movementBlock = (
    <FormRow>
      {field('movementType', 'Movement', [['UNLOAD', 'Unload at plant'], ['LOAD', 'Load for delivery']])}
      {/* Direction follows the movement - what is unloaded here came in, what is loaded goes out -
              so it is derived rather than asked for, and only shown where the gate leaves a choice
              the derivation could get wrong. */}
      {advanced ? field('direction', 'Direction', directions) : <Box />}
    </FormRow>
  );
  // The plate on its own, so the layout can decide what shares its line.
  const vehicleField = (

    <Autocomplete
      freeSolo
      autoHighlight
      selectOnFocus
      handleHomeEndKeys
      disabled={pending}
      options={availableVehicles.map((v) => v.name)}
      inputValue={form.vehicleNumber || ''}
      onChange={(event, chosen) => setPlate(chosen || '')}
      onInputChange={(event, typed) => setPlate(typed)}
      renderInput={(params) => (
        <TextField
          {...params}
          required
          label="Vehicle number"
          inputProps={{ ...params.inputProps, maxLength: 30, style: { textTransform: 'uppercase' } }}
          helperText={registered ? `Registered${registered.hire === 'MARKET' ? ' · hired' : ''}${registered.transporter ? ` · ${registered.transporter}` : ''}` : plate ? 'Not registered; it is admitted as a market vehicle' : 'Type the number on the truck, or choose a registered one'}
        />
      )}
    />
  );
  const movementField = field('movementType', 'Movement', [['UNLOAD', 'Unload at plant'], ['LOAD', 'Load for delivery']]);
  // Movement decides what the truck is here for and the plate says which truck: one question
  // each, neither worth a line of its own on a desk that has a queue outside. Direction is
  // derived from the movement and only appears with the route it belongs to.
  const movementAndVehicle = (
    <FormRow>
      {movementField}
      {vehicleField}
    </FormRow>
  );
  const vehicleBlock = (
    <FormRow columns="2fr 1fr">

      <Autocomplete
        freeSolo
        autoHighlight
        selectOnFocus
        handleHomeEndKeys
        disabled={pending}
        options={availableVehicles.map((v) => v.name)}
        inputValue={form.vehicleNumber || ''}
        onChange={(event, chosen) => setPlate(chosen || '')}
        onInputChange={(event, typed) => setPlate(typed)}
        renderInput={(params) => (
          <TextField
            {...params}
            required
            label="Vehicle number"
            inputProps={{ ...params.inputProps, maxLength: 30, style: { textTransform: 'uppercase' } }}
            helperText={registered ? `Registered${registered.hire === 'MARKET' ? ' · hired' : ''}${registered.transporter ? ` · ${registered.transporter}` : ''}` : plate ? 'Not registered; it is admitted as a market vehicle' : 'Type the number on the truck, or choose a registered one'}
          />
        )}
      />
      {!simple && (
        <TextField
          fullWidth
          label="Fitted GPS device"
          value={fittedSource?.identifier || ''}
          disabled={!fittedSource}
          InputProps={{ readOnly: true }}
          helperText={fittedSource ? 'Fitted to this truck; nothing is issued' : 'None fitted; issue a portable unit below'}
        />
      )}
    </FormRow>
  );

  // What the challan may or may not carry. In the simplified desk it comes last, because a load is
  // admitted without any of it and nobody should read past it to reach a field they must fill.
  const reasonBlock = (
    <TextField
      fullWidth
      disabled={pending}
      label={atGate ? 'Reason for admitting at the gate' : 'Reason for manual intake'}
      value={form.manualReason || ''}
      onChange={(e) => set('manualReason', e.target.value)}
      inputProps={{ maxLength: 500 }}
      helperText="Optional"
    />
  );
  const sourceBlock = field('recordSource', 'Order source', [['MANUAL', 'Manual entry'], ['ERP', 'Imported order']]);
  const extrasBlock = (
    <>
      <FormRow columns="1fr 1fr 2fr">
        {field('quantity', 'Quantity (optional)', undefined, false)}
        {field('quantityUnit', 'Unit', undefined, false)}
        <CatalogField list="material" label="Material (optional)" required={false} disabled={pending} value={form.material} onChange={(v) => set('material', v)} />
      </FormRow>
      {!simple && form.recordSource !== 'ERP' && field('consignmentId', 'Consignment reference (optional)', undefined, false)}
    </>
  );
  const formBody = (
    <Stack spacing={2.5}>
      {error && <Alert severity="error">{error}</Alert>}
      {!form.shipmentId && carriedLane.length > 0 && (
        <Alert
          severity="info"
          action={<Button size="small" onClick={() => setForm(blankDraft(desk))}>Clear</Button>}
        >
          {`Carried from the last truck: ${carriedLane.join(', ')}. Check it still applies.`}
        </Alert>
      )}
      {form.scannedDriver && !form.driverId && <Alert severity="info">{`Driver read from document: ${form.scannedDriver}. Select or register this driver to link the trip.`}</Alert>}
      {/* The feed's own words, then ours. Without the full stop the two run together into
          "failed to fetch The lists below", which reads as one broken sentence. */}
      {loadError && <Alert severity="warning">{`${loadError.replace(/[.\s]*$/, '')}. The lists below may be out of date; the server still checks every selection.`}</Alert>}
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h6">{simple ? 'New trip entry' : '1. Shipment and paperwork'}</Typography>
        {/* Which form this desk fills, changed where it is being filled rather than on a settings
            page two screens away. Anybody who works the desk can change it, because it is the desk's
            own form; it is still the plant's single choice, so every desk changes together. */}
        {operate && (
          <TextField
            select
            size="small"
            label="Form"
            SelectProps={{ SelectDisplayProps: { tabIndex: -1 } }}
            sx={{ minWidth: 150 }}
            value={simple ? 'SIMPLE' : 'GROUPED'}
            disabled={pending || switching}
            onChange={(e) => chooseLayout(e.target.value)}
          >
            <MenuItem value="SIMPLE">Simplified</MenuItem>
            <MenuItem value="GROUPED">Sectioned</MenuItem>
          </TextField>
        )}
        {/* How the form behaves, and what the shipment number is for. Both are read once in
            somebody's first week and never again, and together they pushed the Admit button off a
            900px screen - so they are here for whoever wants them and out of the way of everyone
            who does not. A draft that cannot be saved is a fault rather than guidance, so that one
            stays on the page. */}
        <Tooltip
          title={(
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                The shipment number identifies this load and cannot be used twice. Scan or type
                whatever the driver is carrying beside it. Choose ERP when the order has been
                imported; a manual entry is reconciled when it arrives.
              </Typography>
              <Typography variant="body2">
                Text is saved in this tab for 24 hours. Reattach photographs after reloading.
              </Typography>
            </>
          )}
        >
          <IconButton size="small" aria-label="How this form works">
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Button disabled={pending} onClick={() => { discardDraft(); setForm(blankDraft(desk)); }}>Discard draft</Button>
      </Stack>
      {draftError && <Alert severity="warning">Draft recovery is unavailable in this browser, so nothing typed here is kept if the page reloads.</Alert>}
      {/* The paper in the driver's hand, read rather than retyped where it carries a code, and
          photographed in every case so the trip keeps what was actually presented at the counter. */}
      <Box>
        <FormRow>
          <TextField
            fullWidth
            required={challanDue}
            autoFocus
            disabled={pending}
            label={challanDue ? 'Shipment ID' : 'Shipment ID (can follow)'}
            value={form.shipmentId || ''}
            onChange={(e) => set('shipmentId', e.target.value)}
            inputProps={{ maxLength: 500 }}
          />
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              fullWidth
              disabled={pending}
              label="Challan / order number"
              value={form.challanNumber || ''}
              onChange={(e) => set('challanNumber', e.target.value)}
              inputProps={{ maxLength: 500 }}
              helperText="Optional"
            />
            {scan && <Button variant="outlined" disabled={pending} startIcon={<PhotoCameraIcon />} sx={{ whiteSpace: 'nowrap', height: 56 }} onClick={() => setScanning(true)}>Scan</Button>}
          </Stack>
        </FormRow>
        {form.challanImageDataUrl && (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
            <Box component="img" src={form.challanImageDataUrl} alt="Challan as presented" sx={{ width: 96, height: 64, objectFit: 'cover', borderRadius: 1, border: 1, borderColor: 'divider' }} />
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>The photograph will be kept on this trip.</Typography>
            <Button size="small" disabled={pending} onClick={() => set('challanImageDataUrl', '')}>Remove</Button>
          </Stack>
        )}
      </Box>
      {simple && movementAndVehicle}
      {!simple && sourceBlock}
      {form.recordSource === 'ERP' ? (
        <>
          {!reference && <Alert severity="info">Enter the shipment number, or the number on the paper, to find its imported order.</Alert>}
          {!!reference && !orders.length && <Alert severity="warning">{`No imported order carries ${reference}. Refresh the ERP import, or switch to manual entry.`}</Alert>}
          {orders.length > 1 && field('consignmentId', 'Imported order', orders.map((c) => [c.consignmentId, `${c.consignmentId} · ${c.material || ''} · ${c.quantity ?? ''} ${c.quantityUnit || ''}`]))}
          {order && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">{`Order ${order.consignmentId}`}</Typography>
                {[['Vehicle', order.vehicleNumber], ['Transporter', order.transporter], ['Consignor', order.consignor], ['Material', order.material], ['Quantity', order.quantity != null ? `${order.quantity} ${order.quantityUnit || ''}` : '']].filter(([, value]) => value).map(([name, value]) => (
                  <Stack key={name} direction="row" justifyContent="space-between" gap={2}>
                    <Typography variant="body2" color="text.secondary">{name}</Typography>
                    <Typography variant="body2" fontWeight={600}>{value}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          )}
        </>
      ) : (
        <>
          {!simple && reasonBlock}
          <FormRow>
            <TextField
              fullWidth
              type="datetime-local"
              disabled={pending}
              label="Challan / trip time"
              InputLabelProps={{ shrink: true }}
              value={form.challanAt || ''}
              onChange={(e) => set('challanAt', e.target.value)}
              helperText="Now, unless the paper says otherwise"
            />
            {/* The two halves of the same question, and only one of them applies. A load arriving
                was sent by somebody; a load leaving is carried by somebody. The desk is asked for
                whichever the movement makes it answerable for. */}
            <CatalogField list="supplier" label={form.movementType !== 'UNLOAD' ? 'Supplier (optional)' : challanDue ? 'Supplier' : 'Supplier (can follow)'} required={challanDue && form.movementType === 'UNLOAD'} disabled={pending} value={form.supplier} onChange={(v) => set('supplier', v)} />
          </FormRow>
          <FormRow>
            <CatalogField list="transporter" label={form.movementType !== 'LOAD' ? 'Transporter (optional)' : challanDue ? 'Transporter' : 'Transporter (can follow)'} required={challanDue && form.movementType === 'LOAD'} disabled={pending} value={form.transporter} onChange={(v) => set('transporter', v)} />
            <CatalogField list="consignor" label={challanDue ? 'Consignor / mine' : 'Consignor / mine (can follow)'} required={challanDue} disabled={pending} value={form.consignor} onChange={(v) => set('consignor', v)} />
          </FormRow>
          {!simple && extrasBlock}
          <Alert severity="info">Manual trips stay pending until the matching ERP order is imported, then match or are flagged for an administrator. Each shipment number starts one trip.</Alert>
        </>
      )}
      <FormRow>
        <CatalogField list="consignee" label={challanDue ? 'Consignee' : 'Consignee (can follow)'} required={challanDue} disabled={pending} value={form.consignee} onChange={(v) => set('consignee', v)} />
        <CatalogField
          list="destination"
          label={challanDue ? 'Destination' : 'Destination (can follow)'}
          required={challanDue}
          disabled={pending}
          value={form.destination}
          onChange={(v) => set('destination', v)}
          onPlace={(place) => setForm((current) => ({
            ...current,
            destinationLat: place?.latitude || undefined,
            destinationLon: place?.longitude || undefined,
          }))}
          helperText={form.destinationLat ? 'Found on the map; the road to it can be drawn' : undefined}
        />
      </FormRow>
      {!simple && <Typography variant="h6">2. Vehicle, driver and route</Typography>}
      {!simple && vehicleBlock}
      {busy && <Alert severity="error">{`${registered.name} is already on an open trip (${busy.shipmentId || busy.challanNumber || `trip ${busy.id}`}). Close that trip before admitting it again.`}</Alert>}
      {!registered && !!plate && !fleetWide && <Alert severity="warning">This truck is not registered, and registering one needs access to all vehicles. Ask a tenant administrator to register it.</Alert>}
      {vehicleConflict && <Alert severity="error">{`Challan ${order.challanNumber} is issued to vehicle ${order.vehicleNumber}, not ${vehicle.name}.`}</Alert>}
      <DocumentWarning vehicle={vehicle} prefix={vehicle ? `${vehicle.name}: ` : ''} />
      {!!lapsed.length && field('documentWaiverReason', `Reason for admitting with expired ${lapsed.map((doc) => doc.kind).join(' and ')}`)}
      {fleetWide && <Button size="small" sx={{ alignSelf: 'flex-start' }} onClick={() => setNewVehicle(blankVehicle())}>Truck not listed? Add it</Button>}
      {/* The number on the challan identifies the driver; his name is kept when it is offered. A
          number already on the register fills the name in, and a new one is registered as the truck
          is admitted rather than sending the counter to another screen first. */}
      <FormRow>
        <Autocomplete
          freeSolo
          autoHighlight
          selectOnFocus
          handleHomeEndKeys
          disabled={pending}
          options={data.drivers.map((person) => person.mobile || person.reference).filter(Boolean)}
          inputValue={form.driverMobile || ''}
          onChange={(event, chosen) => setDriverNumber(chosen || '')}
          onInputChange={(event, typed) => setDriverNumber(typed)}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="Driver number"
              inputProps={{ ...params.inputProps, maxLength: 20 }}
              helperText={knownDriver ? `${knownDriver.name} · already registered` : form.driverMobile ? 'Not registered; he is added as the truck is admitted' : challanDue ? 'The number on the challan' : 'Ask the driver — this one is not on the challan'}
            />
          )}
        />
        <TextField
          fullWidth
          disabled={pending}
          label="Driver name (optional)"
          value={form.driverName || ''}
          onChange={(e) => set('driverName', e.target.value)}
          inputProps={{ maxLength: 500 }}
        />
      </FormRow>
      {simple && advanced && field('direction', 'Direction', directions)}
      {simple && extrasBlock}
      {simple && (
        <FormRow>
          {sourceBlock}
          {form.recordSource !== 'ERP' ? field('consignmentId', 'Consignment reference (optional)', undefined, false) : <Box />}
        </FormRow>
      )}
      {simple && form.recordSource !== 'ERP' && reasonBlock}
      {advanced && picker('routeId', 'Route', routes.map((r) => [r.id, r.name]), postedGate && !routes.length
        // A truck is admitted where it comes in. Posted to an exit gate, a desk can record trucks
        // leaving but can never admit one - so saying "define a route that begins here" sends
        // somebody to build a route that the rules would refuse. Say which it is, and offer the one
        // screen that changes it.
        ? { message: `No route starts at ${postedGate.name}, so nothing is admitted here. Define a route that begins at this stage, or change where you are posted.`, action: 'Change where I am posted', path: '/shift-desk' }
        : { message: 'No routes are defined. A route is the gate the truck enters by, the stages in between, and the gate it leaves by. Define one under Configuration.', action: 'Configuration', path: '/configuration' })}
      {!advanced && route && (
        <Typography variant="caption" color="text.secondary">
          {(() => {
            const chosenBy = suggestedRoute && form.routeId === suggestedRoute.route.id ? suggestedRoute
              : gateRoute && form.routeId === gateRoute.route.id ? gateRoute : null;
            return `Route: ${route.name}${chosenBy ? ` — ${chosenBy.why}` : ''}. Open the details below to change it.`;
          })()}
        </Typography>
      )}
      {!!route && <RouteDetail route={route} checkpoints={data.checkpoints} dense={!advanced} />}
      {!advanced && !route && !!routes.length && (
        <Alert severity="warning">No route has been chosen for this load. Open the details below and pick one.</Alert>
      )}
      {advanced && suggestedRoute && form.routeId === suggestedRoute.route.id && (
        <Typography variant="caption" color="text.secondary">{`Chosen from the destination: ${suggestedRoute.why}. Pick another if this load runs differently.`}</Typography>
      )}
      {advanced && postedGate && !!routes.length && (
        <Typography variant="caption" color="text.secondary">{`Routes starting at ${postedGate.name}, where you are posted.`}</Typography>
      )}
      {atGate && route && (entryUsable
        ? <Typography variant="caption" color="text.secondary">{`Entering by ${entryGate.name}, the first stage of this route. It is recorded as you admit the truck.`}</Typography>
        : <Alert severity="warning">{`${route.name} has no first stage to enter by. Check how that route is defined.`}</Alert>)}
      {!allowed.includes(implied) && !!entryGate && (
        <Alert severity="warning">{`${entryGate.name} takes ${String(entryGate.handles).toLowerCase()} traffic only, so this load is recorded as ${String(settled).toLowerCase()}. Admit it at another gate if that is wrong.`}</Alert>
      )}
      {entryGate && (entryGate.handles || 'BOTH') !== 'BOTH' && (
        <Typography variant="caption" color="text.secondary">
          {`${entryGate.name} handles ${entryGate.handles.toLowerCase()} traffic only.`}
        </Typography>
      )}
      {/* The one line that opens what the form decided for the counter. Closed, the page is the
          fields a desk actually fills in; open, it is all of them. */}
      {!simple && movementBlock}
      <Button size="small" sx={{ alignSelf: 'flex-start' }} onClick={() => setAdvanced(!advanced)}>
        {advanced ? 'Hide route and direction' : 'Route and direction'}
      </Button>
      <Typography variant="h6">{simple ? 'Tracking' : '3. Tracking'}</Typography>
      {fitted ? (
        <Alert severity="success">{`${vehicle.name} carries its own GPS device ${fittedSource?.identifier || ''} and already reports position. No portable unit is issued.`}</Alert>
      ) : (
        <>
          {picker('sourceId', 'Portable device to issue', availableSources.map((s) => { const status = reporting(s, data); return [s.id, `${s.identifier} · ${s.kind}${s.kind === 'GPS' ? ` · ${status.text}` : ''}`, s.kind === 'GPS' && !status.ok]; }), gpsUnits.length
            ? { message: `No portable GPS unit is free to issue${deviceDetail ? `: ${deviceDetail}` : ''}. Record a return, or register another unit.`, action: 'Devices', path: '/vehicles' }
            : { message: 'No GPS devices are registered. A tenant administrator adds them under Vehicles, on the Devices tab.', action: 'Devices', path: '/vehicles' })}
          <Alert severity="info">
            {data.trackingConnected
              ? 'Only devices that reported a position in the last 15 minutes can be issued.'
              : 'No tracking provider is connected, so reporting cannot be checked here. Confirm the device is switched on and has a fix before issuing it.'}
          </Alert>
        </>
      )}
    </Stack>
  );
  // The action comes after whatever shows what is still missing: the checklist sits in the sidebar at
  // the parking counter, and inline above the button at the gate.
  const submitButton = (
    <Button type="submit" form={formId} fullWidth size="large" variant="contained" disabled={pending || checklist.some(([, complete]) => !complete)}>
      {pending ? 'Admitting…' : atGate ? 'Admit and record gate in' : 'Start trip at parking'}
    </Button>
  );
  const marks = (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack spacing={1.5}>
        <Typography variant="subtitle1" fontWeight={700}>{atGate ? 'Admission checklist' : 'Intake checklist'}</Typography>
        <Divider />
        {checklist.map(([labelText, complete, note]) => (
          <Stack key={labelText} direction="row" justifyContent="space-between" alignItems="center" gap={1}>
            <Typography variant="body2">{labelText}</Typography>
            <Chip
              size="small"
              label={note || (complete ? 'Ready' : 'Needed')}
              color={note ? 'info' : complete ? 'success' : 'default'}
              variant={note ? 'outlined' : complete ? 'filled' : 'outlined'}
            />
          </Stack>
        ))}
        <Divider />
        {!challanDue && (
          <Typography variant="caption" color="text.secondary">
            The challan can follow. Enter it on the trip when it arrives — the plant gate will not
            take this truck in until it is on there.
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">{fitted ? 'This truck is tracked by its own fitted device; nothing is issued.' : 'A portable device is checked out temporarily. Record its physical return after the trip closes.'}</Typography>
      </Stack>
    </Paper>
  );
  const dialog = (
    <>
      <Dialog open={!!fittedNotice} onClose={() => setFittedNotice('')} maxWidth="xs" fullWidth>
        <DialogTitle>This truck carries its own GPS</DialogTitle>
        <DialogContent>
          <DialogContentText>{fittedNotice}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setFittedNotice('')}>Continue</Button>
        </DialogActions>
      </Dialog>
      <AddVehicleDialog
        open={!!newVehicle}
        draft={newVehicle}
        onChange={setNewVehicle}
        onClose={() => setNewVehicle(null)}
        onCreated={(created) => { setForm((current) => ({ ...current, vehicleId: created.id, driverId: created.driverId || current.driverId })); setNewVehicle(null); }}
      />
      <ScanChallan
        open={scanning}
        onClose={() => setScanning(false)}
        onScanned={(reading) => setForm((current) => applyScan(current, reading))}
      />
    </>
  );
  if (compact) {
    return (
      <Box component="form" onSubmit={submit}>
        <Stack spacing={2.5}>
          {formBody}
          {marks}
          {submitButton}
        </Stack>
        {dialog}
      </Box>
    );
  }
  return (
    <>
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="flex-start">
        {/* The button sits with the checklist rather than at the foot of the form. Twenty fields is
            about 1,400 pixels and a gate screen is 900, so Admit was always below the fold: a desk
            filled the last box and then scrolled to act on it. The checklist is already pinned, and
            what is still missing and the button that acts on it belong in the same place. It stays a
            submit button for this form through `form`, so Enter and the button do the same thing. */}
        <Paper component="form" id={formId} onSubmit={submit} variant="outlined" sx={{ p: { xs: 2, md: 3 }, flex: 1, minWidth: 0, width: '100%' }}>
          <Stack spacing={2.5}>
            {formBody}
          </Stack>
        </Paper>
        <Stack spacing={3} sx={{ width: { xs: '100%', lg: 300 }, position: { lg: 'sticky' }, top: { lg: 24 } }}>
          {marks}
          {submitButton}
          {sidebar}
        </Stack>
      </Stack>
      {dialog}
    </>
  );
};

export default AdmitTruck;
